// 設定開關與秒數選項：綠色霧面軌道、米白圓鈕、紅色選中選項。
// === 使用圖片 ===
// 軌道與選項間接使用 dialog-skin 的紅綠插畫素材；圓鈕與文字由 Arc / Text 繪製。

import Phaser from 'phaser';
import { animateTo, Motion } from './motion';
import { CArcadeButtonSurface } from './arcade-button-surface';
import { playUIClick } from '@audio/scene-audio';
import { CDialogButtonSurface } from './dialog-skin';
import { ZH_TW } from '@i18n/zh-tw';

// ==== 版面常數 ====
const THUMB_INSET = 5; // 圓鈕與軌道邊緣間距
const THUMB_COLOR = 0xfff3d1; // 米白圓鈕
const THUMB_PRESSED_COLOR = 0xd8c69d; // 按下狀態
const THUMB_BORDER_COLOR = 0xd7c79f; // 奶油深色細邊
const THUMB_BORDER_WIDTH = 1; // 圓鈕邊框寬
const SWITCH_OFF_ALPHA = 0.6; // 關閉軌道亮度
const SWITCH_LABEL_FONT_SIZE = 20; // 開關文字字級
const SWITCH_LABEL_OFFSET = 18; // 文字避開圓鈕
const LABEL_STROKE = '#063427'; // 選項文字描邊
const LABEL_STROKE_WIDTH = 0; // 選項描邊寬
const LABEL_FONT_SIZE = 22; // 分段選項文字
const ACTIVE_TEXT_COLOR = '#fff3df'; // 暖白文字
const INACTIVE_TEXT_COLOR = '#e0eadf'; // 非選中選項文字

export const SettingsControlKind = { Switch: 'switch', Segment: 'segment' } as const;
export type TSettingsControlKind = (typeof SettingsControlKind)[keyof typeof SettingsControlKind];

export interface ISettingsControlConfig {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly kind: TSettingsControlKind;
  readonly selected: boolean;
  readonly label?: string;
  readonly onActivate: () => void;
}

export class CSettingsControl extends Phaser.GameObjects.Container {
  private readonly rail?: CDialogButtonSurface;
  private readonly thumb?: Phaser.GameObjects.Arc;
  private readonly segment?: CArcadeButtonSurface;
  private readonly label: Phaser.GameObjects.Text;
  private selected: boolean;
  private hovered = false;
  private focused = false;
  private pressed = false;
  private thumbTarget: number | undefined;

  constructor(
    scene: Phaser.Scene,
    private readonly config: ISettingsControlConfig
  ) {
    super(scene, config.x, config.y);
    this.selected = config.selected;
    if (config.kind === SettingsControlKind.Switch) {
      this.rail = new CDialogButtonSurface(scene, 0, 0, config.width, config.height, false);
      this.thumb = scene.add
        .circle(0, 0, config.height / 2 - THUMB_INSET, THUMB_COLOR)
        .setStrokeStyle(THUMB_BORDER_WIDTH, THUMB_BORDER_COLOR);
      this.add(this.rail);
    }
    if (config.kind === SettingsControlKind.Segment) {
      this.segment = new CArcadeButtonSurface(scene, config.width, config.height);
      this.add(this.segment);
    }
    this.label = scene.add
      .text(0, 0, '', {
        fontFamily: 'sans-serif',
        fontSize: `${LABEL_FONT_SIZE}px`,
        fontStyle: 'bold',
        stroke: LABEL_STROKE,
        strokeThickness: LABEL_STROKE_WIDTH,
      })
      .setOrigin(0.5);
    const hit = scene.add
      .zone(0, 0, config.width, config.height)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => {
      this.hovered = true;
      this.redraw();
    });
    hit.on('pointerout', () => {
      this.hovered = false;
      this.pressed = false;
      this.redraw();
    });
    hit.on('pointerdown', () => {
      this.pressed = true;
      this.redraw();
    });
    hit.on('pointerupoutside', () => {
      this.pressed = false;
      this.redraw();
    });
    hit.on('pointerup', () => {
      const activate = this.pressed;
      this.pressed = false;
      this.redraw();
      if (activate) this.activate();
    });
    this.add(this.label);
    if (this.thumb) this.add(this.thumb);
    this.add(hit);
    this.setSize(config.width, config.height);
    scene.add.existing(this);
    this.redraw();
  }

  activate(): void {
    if (!this.scene) return;
    playUIClick(this.scene);
    this.config.onActivate();
  }

  setSelected(selected: boolean): this {
    this.selected = selected;
    this.redraw();
    return this;
  }

  setFocused(focused: boolean): this {
    this.focused = focused;
    this.redraw();
    return this;
  }

  private redraw(): void {
    if (!this.scene) return;
    const { width, height, kind } = this.config;
    const highlighted = this.hovered || this.focused;
    const isSwitch = kind === SettingsControlKind.Switch;
    if (isSwitch) {
      this.rail?.setAppearance(false, highlighted, this.pressed);
      this.rail?.setAlpha(this.selected || highlighted ? 1 : SWITCH_OFF_ALPHA);
      const thumbX = ((width - height) / 2) * (this.selected ? 1 : -1);
      if (this.thumb) {
        if (this.thumbTarget === undefined) this.thumb.setX(thumbX);
        else if (this.thumbTarget !== thumbX)
          animateTo(this.scene, this.thumb, { x: thumbX }, Motion.switchMs);
        this.thumbTarget = thumbX;
        this.thumb.setFillStyle(this.pressed ? THUMB_PRESSED_COLOR : THUMB_COLOR);
      }
      this.label
        .setText(this.selected ? ZH_TW.settings.on : ZH_TW.settings.off)
        .setFontSize(SWITCH_LABEL_FONT_SIZE)
        .setColor(this.selected ? ACTIVE_TEXT_COLOR : INACTIVE_TEXT_COLOR)
        .setX(this.selected ? -SWITCH_LABEL_OFFSET : SWITCH_LABEL_OFFSET);
      return;
    }
    this.segment?.setAppearance(this.selected, highlighted, this.pressed);
    this.label.setColor(this.selected ? ACTIVE_TEXT_COLOR : INACTIVE_TEXT_COLOR);
    this.label.setText(this.config.label ?? '');
  }
}
